<template>
  <base-modal
    :isOpen="isOpen"
    persistent
    width="500"
    :title="modal.title"
    @close="onClose"
    :icon="{
      name: modal.icon,
      color: modal.iconColor,
      bgColor: modal.iconBgColor,
    }"
  >
    <div class="grey-section rounded-lg pa-2 mt-4">
      <div class="text-grey5 text-subtitle-1">{{ $t("Call Id") }}:</div>
      <base-chip-material
        :text="callerInfo.callid"
        color="info"
        variant="flat"
        append-icon="solar:hashtag-circle-bold"
      />
    </div>

    <v-form ref="addContactForm" @submit.prevent="submitForm" class="my-6">
      <div v-for="item in fields" class="mt-3">
        <span class="input-label">
          {{ $t(item.label) }}
          <sup class="text-error"> * </sup>
        </span>
        <base-form-text-field
          v-model="formData[item.value]"
          :isNumber="item.isNumber"
          :rules="item.rules"
          placeholder="Enter"
        />
      </div>
      <contact-call-reason-form />
      <button type="submit" class="hidden" />
    </v-form>

    <template #footer>
      <base-button
        name="Submit"
        variant="flat"
        :disabled="!validForm"
        :loading="loading"
        @click="submitForm"
        type="submit"
        class="w-80"
      />
    </template>
  </base-modal>
</template>

<script setup>
import { useContactStore } from "~/stores/contact";
import { useContactFormStore } from "~/stores/contact/form";
import { useContactCallStore } from "~/stores/contact/call";
import { requiredRule, integer, phone } from "~/utils/validation";
import { dataFormatterToSendServer } from "~/utils/helpers/ciFormatter";

const contactStore = useContactStore();
const contactFormStore = useContactFormStore();
const contactCallStore = useContactCallStore();

const props = defineProps({
  isOpen: Boolean,
  type: String,
});

const callerInfo = computed(() => contactCallStore.callerInfo);
const emit = defineEmits(["close"]);

const loading = ref(false);
const addContactForm = ref(null);
const validForm = computed(() => {
  if (!addContactForm.value) return false;
  return addContactForm.value.isValid;
});

const formData = ref({});
const submitForm = async () => {
  const { valid } = await addContactForm.value.validate();
  if (!valid) return;

  loading.value = true;
  const fields = dataFormatterToSendServer(
    formData.value,
    contactFormStore[`${props.type}Form`]
  );
  const contactInfo = {
    form_type: props.type,
    values: fields,
  };
  await contactStore.submitData(contactInfo);
  await contactCallStore.submitCallReason(false);
  await contactCallStore.submitReminder(contactInfo.values);
  onClose();
};

const onClose = () => {
  loading.value = false;
  addContactForm.value.reset();
  formData.value = {};
  contactCallStore.resetReasonInfo();
  emit("close");
};

const modal = computed(() => {
  switch (props.type) {
    case "people":
      return {
        title: "Add New Person",
        icon: "solar:user-rounded-outline",
        iconColor: "primary5",
        iconBgColor: "primary1",
      };
    case "companies":
      return {
        title: "Add New Company",
        icon: "solar:buildings-outline",
        iconColor: "success5",
        iconBgColor: "success1",
      };
    default:
      return {
        title: "",
        icon: "",
        iconColor: "",
        iconBgColor: "",
      };
  }
});

const fields = computed(() =>
  props.type == "people" ? personFields : companyFields
);

const personFields = [
  {
    label: "Main Phone Number",
    value: "phone_number",
    rules: [requiredRule, integer, phone],
    isNumber: true,
  },
  {
    label: "FullName",
    value: "name",
    rules: [requiredRule],
  },
];

const companyFields = [
  {
    label: "Main Phone Number",
    value: "phone_number",
    rules: [requiredRule, integer, phone],
    isNumber: true,
  },
  {
    label: "Company Name",
    value: "company_name",
    rules: [requiredRule],
  },
  {
    label: "Agent Fullname",
    value: "name",
    rules: [requiredRule],
  },
];

watch(
  () => props.isOpen,
  async (newValue) => {
    if (newValue) {
      formData.value.phone_number = callerInfo.value.phoneNumber || "";
    }
  }
);
</script>

<style scoped>
.grey-section {
  display: flex;
  justify-content: space-between;
  background-color: rgb(var(--v-theme-greyLightest));
  border: 1px solid rgb(var(--v-theme-grey3));
}
</style>
